/**
 * Product Share Dialog Component
 * Handles the share with a friend functionality via EmailJS
 */

class ProductShareDialog {
  constructor(trigger) {
    this.trigger = trigger;
    this.dialog = null;
    this.form = null;

    // EmailJS configuration
    this.config = {
      publicKey: "PreDcsoCs5-KXWKE1",
      serviceId: "service_5tqyrkt",
      templateId: "template_f7crath",
    };

    // Rate limiting (10 emails per hour per user)
    this.rateLimitKey = "emailjs_rate_limit";
    this.maxEmails = 10;
    this.timeWindow = 3600000; // 1 hour

    this.init();
  }

  async init() {
    // Load EmailJS SDK if not already loaded
    if (typeof emailjs === "undefined") {
      await this.loadEmailJS();
    }

    // Initialize EmailJS
    emailjs.init(this.config.publicKey);

    this.createDialog();
    this.bindEvents();
  }

  loadEmailJS() {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src =
        "https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js";
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  createDialog() {
    // Create dialog HTML
    const dialogHTML = `
      <dialog class="product-share-dialog" aria-labelledby="share-dialog-title" aria-modal="true">
        <div class="product-share-dialog__content">
          <header class="product-share-dialog__header">
            <h3 id="share-dialog-title" class="product-share-dialog__title">Share this product</h3>
            <button type="button" class="product-share-dialog__close" aria-label="Close dialog">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </header>

          <form class="product-share-form">
            <div class="product-share-form__field">
              <label for="share-your-name" class="product-share-form__label">Your name *</label>
              <input
                type="text"
                id="share-your-name"
                name="yourName"
                class="product-share-form__input"
                required
                maxlength="100"
                placeholder="Your name"
              />
            </div>

            <div class="product-share-form__field">
              <label for="share-your-email" class="product-share-form__label">Your email *</label>
              <input
                type="email"
                id="share-your-email"
                name="yourEmail"
                class="product-share-form__input"
                required
                maxlength="254"
                placeholder="your@email.com"
              />
            </div>

            <div class="product-share-form__field">
              <label for="share-friend-name" class="product-share-form__label">Friend's name</label>
              <input
                type="text"
                id="share-friend-name"
                name="friendName"
                class="product-share-form__input"
                maxlength="100"
                placeholder="How should we address them? (Optional)"
              />
            </div>

            <div class="product-share-form__field">
              <label for="share-friend-email" class="product-share-form__label">Friend's email *</label>
              <input
                type="email"
                id="share-friend-email"
                name="friendEmail"
                class="product-share-form__input"
                required
                maxlength="254"
                placeholder="friend@email.com"
              />
            </div>

            <div class="product-share-form__field">
              <label for="share-note" class="product-share-form__label">Personal message</label>
              <textarea
                id="share-note"
                name="note"
                class="product-share-form__textarea"
                rows="4"
                maxlength="500"
                placeholder="I thought you'd love this..."
              ></textarea>
              <small class="char-count">0/500</small>
            </div>

            <!-- Rate limit warning -->
            <div class="rate-limit-notice" style="display: none;">
              <small class="rate-limit-text"></small>
            </div>

            <div class="product-share-form__actions">
              <button type="button" class="product-share-form__button product-share-form__button--secondary">
                Cancel
              </button>
              <button type="submit" class="product-share-form__button product-share-form__button--primary">
                Send Hint
              </button>
            </div>
          </form>
        </div>
      </dialog>
    `;

    // Insert dialog into document
    document.body.insertAdjacentHTML("beforeend", dialogHTML);
    this.dialog = document.querySelector(".product-share-dialog");
    this.form = this.dialog?.querySelector(".product-share-form");

    this.setupCharacterCounter();
  }

  setupCharacterCounter() {
    const textarea = this.form?.querySelector('[name="note"]');
    const counter = this.form?.querySelector(".char-count");

    textarea?.addEventListener("input", (e) => {
      const count = e.target.value.length;
      counter.textContent = `${count}/500`;

      if (count > 450) {
        counter.style.color = "#f44336";
      } else {
        counter.style.color = "#666";
      }
    });
  }

  getProductData() {
    return {
      title:
        document.querySelector("[data-product-title]")?.textContent?.trim() ||
        document.querySelector("h1")?.textContent?.trim() ||
        "Product",
      url: window.location.href,
      price:
        document.querySelector("[data-product-price]")?.textContent?.trim() ||
        document.querySelector(".price")?.textContent?.trim() ||
        "",
      image: this.getProductImage(),
      variant: this.getSelectedVariant(),
    };
  }

  getProductImage() {
    // Try multiple methods to get the product image
    let imageUrl = null;

    // Method 1: Look for data-product-image attribute
    const dataImage = document.querySelector("[data-product-image]");
    if (dataImage) {
      imageUrl =
        dataImage.getAttribute("src") ||
        dataImage.getAttribute("data-src") ||
        dataImage.getAttribute("data-original");
    }

    // Method 2: Look for product images by class/alt text
    if (!imageUrl) {
      const productImg = document.querySelector(
        'img[alt*="product" i], .product-image img, .product__image img, [class*="product-image"] img, .product-media img',
      );
      if (productImg) {
        imageUrl =
          productImg.getAttribute("src") ||
          productImg.getAttribute("data-src") ||
          productImg.getAttribute("data-original");
      }
    }

    // Method 3: Look for Shopify-specific image patterns
    if (!imageUrl) {
      const shopifyImg = document.querySelector(
        'img[src*="cdn.shopify.com"], img[src*=".myshopify.com"]',
      );
      if (shopifyImg) {
        imageUrl = shopifyImg.getAttribute("src");
      }
    }

    // Method 4: Look for the first image in a product gallery
    if (!imageUrl) {
      const galleryImg = document.querySelector(
        '.product-gallery img, .product__gallery img, [class*="gallery"] img',
      );
      if (galleryImg) {
        imageUrl =
          galleryImg.getAttribute("src") ||
          galleryImg.getAttribute("data-src") ||
          galleryImg.getAttribute("data-original");
      }
    }

    // Method 5: Look for any large image on the page (likely product image) - skip localhost URLs
    if (!imageUrl) {
      const images = document.querySelectorAll("img");
      for (const img of images) {
        const src = img.getAttribute("src") || img.getAttribute("data-src");
        if (
          src &&
          !src.includes("127.0.0.1") &&
          !src.includes("localhost") &&
          img.naturalWidth > 200 &&
          img.naturalHeight > 200
        ) {
          imageUrl = src;
          break;
        }
      }
    }

    // Convert relative URLs to absolute URLs - but use production domain
    if (imageUrl && imageUrl.startsWith("/")) {
      // Use the production Shopify domain for relative URLs
      imageUrl = "https://tovaa-store.myshopify.com" + imageUrl;
    }

    return imageUrl || "";
  }

  sanitizeImageUrl(imageUrl) {
    if (!imageUrl) return "";

    // Log original URL for debugging
    console.log("Original image URL:", imageUrl);
    console.log("URL contains tovaa.com:", imageUrl.includes("tovaa.com"));
    console.log(
      "URL contains myshopify.com:",
      imageUrl.includes("myshopify.com"),
    );

    let cleanUrl = imageUrl;

    // Fix localhost/development URLs and live site domain mixing
    if (cleanUrl.includes("127.0.0.1") || cleanUrl.includes("localhost")) {
      // Extract the path after .myshopify.com from localhost proxy
      // Pattern: http://127.0.0.1:9292//tovaa-store.myshopify.com/cdn/shop/files/filename.jpg
      const pathMatch = cleanUrl.match(
        /\.myshopify\.com(\/cdn\/shop\/files\/.*)/,
      );
      if (pathMatch) {
        cleanUrl = "https://tovaa-store.myshopify.com" + pathMatch[1];
        console.log("Fixed localhost URL to:", cleanUrl);
      }
    }

    // Fix live site URLs that mix tovaa.com with tovaa-store.myshopify.com
    if (cleanUrl.includes("tovaa.com/cdn/shop/files/")) {
      // Extract just the filename and path
      const cdnPathMatch = cleanUrl.match(/(\/cdn\/shop\/files\/.*)/);
      if (cdnPathMatch) {
        cleanUrl = "https://tovaa-store.myshopify.com" + cdnPathMatch[1];
        console.log("Fixed mixed domain URL from:", imageUrl);
        console.log("Fixed mixed domain URL to:", cleanUrl);
      }
    }

    // Remove duplicate slashes in path (except after protocol)
    cleanUrl = cleanUrl.replace(/([^:]\/)\/+/g, "$1");

    // Prevent duplicate domains and mixed domain issues
    // Pattern: tovaa-store.myshopify.com/tovaa-store.myshopify.com/...
    cleanUrl = cleanUrl.replace(
      /https:\/\/([^\/]+\.myshopify\.com)\/\1\//,
      "https://$1/",
    );

    // Pattern: tovaa-store.myshopify.com/tovaa.com/...
    cleanUrl = cleanUrl.replace(
      /https:\/\/tovaa-store\.myshopify\.com\/tovaa\.com\//,
      "https://tovaa-store.myshopify.com/",
    );

    // Remove query parameters that might cause issues
    cleanUrl = cleanUrl.split("?")[0];

    // Ensure it's a proper HTTP/HTTPS URL
    if (cleanUrl.startsWith("//")) {
      cleanUrl = "https:" + cleanUrl;
    }

    // Limit URL length (EmailJS might have limits)
    if (cleanUrl.length > 250) {
      console.warn("Image URL too long, truncating:", cleanUrl.length);
      cleanUrl = "";
    }

    // Check for problematic characters
    if (/[<>"'`]/.test(cleanUrl)) {
      console.warn("Image URL contains dangerous characters, removing");
      cleanUrl = "";
    }

    // Validate it's a proper URL
    try {
      new URL(cleanUrl);
    } catch (e) {
      console.warn("Invalid URL format, removing:", cleanUrl);
      cleanUrl = "";
    }

    console.log("Final sanitized image URL:", cleanUrl);
    console.log(
      "Final URL is valid CDN:",
      cleanUrl.includes("tovaa-store.myshopify.com/cdn/shop/files/"),
    );
    return cleanUrl;
  }

  getSelectedVariant() {
    // Try to get selected variant information
    const variantSelect = document.querySelector('[name="id"]');
    if (variantSelect && variantSelect.options) {
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      return {
        id: variantSelect.value || "",
        title: selectedOption?.textContent || "",
      };
    }

    // Fallback: look for variant picker buttons
    const selectedVariantButton = document.querySelector(
      '.variant-picker__button[aria-pressed="true"]',
    );
    return {
      id: selectedVariantButton?.dataset?.variantId || "",
      title: selectedVariantButton?.textContent || "",
    };
  }

  bindEvents() {
    // Open dialog when trigger is clicked
    this.trigger.addEventListener("click", (e) => {
      e.preventDefault();
      this.openDialog();
    });

    // Close dialog events
    const closeButton = this.dialog?.querySelector(
      ".product-share-dialog__close",
    );
    const cancelButton = this.dialog?.querySelector(
      ".product-share-form__button--secondary",
    );

    closeButton?.addEventListener("click", () => this.closeDialog());
    cancelButton?.addEventListener("click", () => this.closeDialog());

    // Close on backdrop click
    this.dialog?.addEventListener("click", (e) => {
      if (e.target === this.dialog) {
        this.closeDialog();
      }
    });

    // Close on Escape key
    this.dialog?.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.closeDialog();
      }
    });

    // Handle form submission
    this.form?.addEventListener("submit", (e) => {
      e.preventDefault();
      this.handleSubmit(e);
    });
  }

  openDialog() {
    // Show dialog
    if (this.dialog && typeof this.dialog.showModal === "function") {
      this.dialog.showModal();
    }
    document.body.classList.add("dialog-open");

    // Reset scroll position to top
    const formElement = this.dialog?.querySelector(".product-share-form");
    if (formElement) {
      formElement.scrollTop = 0;
    }

    // Focus first input with better timing
    const firstInput = this.dialog?.querySelector('input[type="text"]');
    if (firstInput && typeof firstInput.focus === "function") {
      setTimeout(() => {
        firstInput.focus();
        // Ensure the input is in view
        firstInput.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
    }
  }

  closeDialog() {
    if (this.dialog && typeof this.dialog.close === "function") {
      this.dialog.close();
    }
    document.body.classList.remove("dialog-open");

    // Reset form
    if (this.form && typeof this.form.reset === "function") {
      this.form.reset();
    }

    // Reset character counter
    const counter = this.form?.querySelector(".char-count");
    if (counter) counter.textContent = "0/500";

    // Hide rate limit notice
    const notice = this.form?.querySelector(".rate-limit-notice");
    if (notice) notice.style.display = "none";
  }

  checkRateLimit() {
    const now = Date.now();
    const rateLimitData = this.getRateLimitData();

    // Clean old entries
    const validEntries = rateLimitData.filter(
      (timestamp) => now - timestamp < this.timeWindow,
    );

    if (validEntries.length >= this.maxEmails) {
      const oldestEntry = Math.min(...validEntries);
      const resetTime = Math.ceil(
        (oldestEntry + this.timeWindow - now) / 1000 / 60,
      );

      return {
        allowed: false,
        resetTime: resetTime,
      };
    }

    return { allowed: true };
  }

  getRateLimitData() {
    try {
      const data = localStorage.getItem(this.rateLimitKey);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  updateRateLimit() {
    const now = Date.now();
    const rateLimitData = this.getRateLimitData();
    rateLimitData.push(now);

    // Keep only recent entries
    const validEntries = rateLimitData.filter(
      (timestamp) => now - timestamp < this.timeWindow,
    );

    try {
      localStorage.setItem(this.rateLimitKey, JSON.stringify(validEntries));
    } catch {
      // Storage full, clear old data
      localStorage.removeItem(this.rateLimitKey);
    }
  }

  validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return false;

    // Block obvious fake/disposable emails
    const disposableProviders = [
      "10minutemail",
      "tempmail",
      "guerrillamail",
      "mailinator",
    ];

    const domain = email.split("@")[1]?.toLowerCase();
    return !disposableProviders.some((provider) => domain?.includes(provider));
  }

  async handleSubmit(event) {
    event.preventDefault();

    // Check rate limit
    const rateCheck = this.checkRateLimit();
    if (!rateCheck.allowed) {
      this.showRateLimitError(rateCheck.resetTime);
      return;
    }

    if (!this.form) return;
    const formData = new FormData(this.form);
    const productData = this.getProductData();

    // Validate emails
    const yourEmail = formData.get("yourEmail");
    const friendEmail = formData.get("friendEmail");

    if (!this.validateEmail(yourEmail) || !this.validateEmail(friendEmail)) {
      this.showError("Please enter valid email addresses.");
      return;
    }

    const templateParams = {
      from_name: formData.get("yourName"),
      from_email: yourEmail,
      to_name: formData.get("friendName") || "Friend",
      to_email: friendEmail,
      reply_to: yourEmail,
      message: formData.get("note") || "",
      product_title: productData.title,
      product_price: productData.price,
      product_url: productData.url,
      product_image: this.sanitizeImageUrl(productData.image),
      shop_name: "Tovaa", // Replace with your actual store name
    };

    const submitBtn = this.form?.querySelector('[type="submit"]');
    if (submitBtn) {
      submitBtn.textContent = "Sending...";
      submitBtn.disabled = true;
    }

    // Debug logging
    console.log("EmailJS Config:", this.config);
    console.log("Template Parameters:", templateParams);
    console.log("Friend Email (to_email):", templateParams.to_email);
    console.log("Your Email (from_email):", templateParams.from_email);
    console.log("=== IMAGE URL DEBUG ===");
    console.log("Original Product Image URL:", productData.image);
    console.log("Sanitized Product Image URL:", templateParams.product_image);
    console.log(
      "Original URL length:",
      productData.image ? productData.image.length : 0,
    );
    console.log(
      "Sanitized URL length:",
      templateParams.product_image ? templateParams.product_image.length : 0,
    );
    console.log(
      "Original has localhost:",
      productData.image ? productData.image.includes("127.0.0.1") : false,
    );
    console.log(
      "Sanitized has localhost:",
      templateParams.product_image
        ? templateParams.product_image.includes("127.0.0.1")
        : false,
    );
    console.log(
      "Final image URL valid:",
      templateParams.product_image
        ? templateParams.product_image.startsWith("https://")
        : false,
    );
    console.log("=====================");

    try {
      const result = await emailjs.send(
        this.config.serviceId,
        this.config.templateId,
        templateParams,
      );

      console.log("EmailJS Success:", result);

      // Update rate limit
      this.updateRateLimit();

      this.showSuccessMessage("Hint sent successfully!");
      this.closeDialog();
    } catch (error) {
      console.error("EmailJS error:", error);
      console.error("Template params:", templateParams);

      let errorMessage = "Failed to send hint. Please try again.";
      if (error.status === 422) {
        errorMessage =
          "Email configuration error. Please check email addresses.";
      } else if (error.status === 402) {
        errorMessage =
          "Service temporarily unavailable. Please try again later.";
      } else if (error.status === 400) {
        errorMessage = "Invalid email addresses. Please check and try again.";
      }

      this.showError(errorMessage);
    } finally {
      if (submitBtn) {
        submitBtn.textContent = "Send Hint";
        submitBtn.disabled = false;
      }
    }
  }

  showSuccessMessage(message = "Product shared successfully!") {
    this.showNotification(message, "success");
  }

  showError(message) {
    this.showNotification(message, "error");
  }

  showRateLimitError(resetTime) {
    const notice = this.form?.querySelector(".rate-limit-notice");
    const text = this.form?.querySelector(".rate-limit-text");

    if (notice && text) {
      text.textContent = `Rate limit reached. Please wait ${resetTime} minutes before sending another hint.`;
      notice.style.display = "block";
      notice.style.color = "#f44336";
    }
  }

  showNotification(message, type) {
    const notification = document.createElement("div");
    notification.className = `hint-notification ${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 16px 24px;
      border-radius: 0px;
      color: white;
      font-weight: 500;
      z-index: 10000;
      max-width: 300px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      background: ${type === "success" ? "var(--color-primary, #000)" : "#f44336"};
    `;

    document.body.appendChild(notification);

    // Animate in
    notification.style.transform = "translateX(100%)";
    notification.style.transition = "transform 0.3s ease";
    setTimeout(() => {
      notification.style.transform = "translateX(0)";
    }, 10);

    // Remove after delay
    setTimeout(() => {
      notification.style.transform = "translateX(100%)";
      setTimeout(() => notification.remove(), 300);
    }, 4000);
  }
}

// CSS Styles for the dialog
const dialogStyles = `
  <style>
    .product-share-dialog {
      border: none;
      border-radius: 0px;
      padding: 0;
      max-width: 500px;
      width: 90vw;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      background: var(--color-background, #fff);
      color: var(--color-foreground, #000);
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      margin: 0;
    }

    .product-share-dialog::backdrop {
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(2px);
    }

    .product-share-dialog__content {
      padding: 0;
      display: flex;
      flex-direction: column;
      max-height: 90vh;
    }

    .product-share-dialog__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 24px 24px 16px;
      border-bottom: 1px solid var(--color-border, #e5e5e5);
    }

    .product-share-dialog__title {
      margin: 0;
      font-size: 1.25rem;

    }

    .product-share-dialog__close {
      background: none;
      border: none;
      cursor: pointer;
      padding: 8px;
      border-radius: 0px;
      color: var(--color-foreground, #000);
      transition: background-color 0.2s ease;
    }

    .product-share-dialog__close:hover {
      background: var(--color-background-secondary, #f5f5f5);
    }

    .product-share-form {
      padding: 24px;
      flex: 1;
      overflow-y: auto;
      max-height: calc(90vh - 80px);
    }

    .product-share-form__field {
      margin-bottom: 20px;
    }

    .product-share-form__label {
      display: block;
      margin-bottom: 6px;
      font-weight: 500;
      color: var(--color-foreground, #000);
    }

    .product-share-form__input,
    .product-share-form__textarea {
      width: 100%;
      padding: 12px 16px;
      border: 1px solid var(--color-border, #d1d5db);
      border-radius: 0px;
      font-size: 16px;
      font-family: inherit;
      background: var(--color-background, #fff);
      color: var(--color-foreground, #000);
      transition: border-color 0.2s ease, box-shadow 0.2s ease;
    }

    .product-share-form__input:focus,
    .product-share-form__textarea:focus {
      outline: none;
      border-color: var(--color-primary, #000);
      box-shadow: 0 0 0 3px var(--color-primary-alpha, rgba(0, 0, 0, 0.1));
    }

    .product-share-form__textarea {
      resize: vertical;
      min-height: 100px;
    }

    .char-count {
      display: block;
      text-align: right;
      margin-top: 4px;
      font-size: 12px;
      color: #666;
    }

    .rate-limit-notice {
      background: #fff3cd;
      border: 1px solid #ffeaa7;
      border-radius: 0px;
      padding: 12px;
      margin-bottom: 16px;
    }

    .product-share-form__actions {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      margin-top: 24px;
      padding: 20px 0 0 0;
      border-top: 1px solid var(--color-border, #e5e5e5);
      position: sticky;
      bottom: 0;
      background: var(--color-background, #fff);
    }

    .product-share-form__button {
      padding: 12px 24px;
      border: none;
      border-radius: 0px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      min-width: 100px;
      text-transform: uppercase;
    }

    .product-share-form__button--primary {
      background: var(--color-primary, #000);
      color: var(--color-primary-text, #fff);
    }

    .product-share-form__button--primary:hover:not(:disabled) {
      background: var(--color-primary-hover, #333);
    }

    .product-share-form__button--primary:disabled {
      background: #ccc;
      cursor: not-allowed;
    }

    .product-share-form__button--secondary {
      background-color: white;
      border: 1px solid var(--color-primary, #000);
      color: var(--color-primary, #000);
    }

    .product-share-form__button--secondary:hover {
      background: var(--color-background-tertiary, #e5e5e5);
    }

    .dialog-open {
      overflow: hidden;
      position: fixed;
      width: 100%;
    }

    @keyframes slideIn {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    @media (max-width: 640px) {
      .product-share-dialog {
        width: 95vw;
        height: 95vh;
        max-height: 95vh;
        margin: 0;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      .product-share-form {
        max-height: calc(95vh - 120px);
        padding: 16px;
      }

      .product-share-form__actions {
        flex-direction: column;
        padding: 16px 0 0 0;
      }

      .product-share-form__button {
        width: 100%;
      }

      .product-share-dialog__header {
        padding: 16px;
      }
    }
  </style>
`;

// Initialize share dialogs when DOM is loaded
document.addEventListener("DOMContentLoaded", function () {
  // Add styles to head
  document.head.insertAdjacentHTML("beforeend", dialogStyles);

  // Initialize all share buttons
  const shareButtons = document.querySelectorAll("[data-share-trigger]");
  shareButtons.forEach((button) => {
    new ProductShareDialog(button);
  });
});

// Export for use in modules if needed
if (typeof module !== "undefined" && module.exports) {
  module.exports = ProductShareDialog;
}
